<script lang="ts">
	import classNames from 'classnames';
	import type { EmblaCarouselType } from 'embla-carousel';
	import emblaCarouselSvelte from 'embla-carousel-svelte';
	import { onDestroy } from 'svelte';
	import { HorizontalScrollControls } from '#lib/components/HorizontalScrollControls/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { emblaScrollHint } from '#lib/utils/emblaScrollHint/index.js';
	import {
		initialHorizontalScrollState,
		type HorizontalScrollState,
	} from '#lib/utils/horizontalScrollHint/index.js';
	import { TalksRoutes } from '#lib/websites/talks/constants/routes.js';
	import { page } from '$app/state';
	import { ApplePodcastsIcon } from '$global/assets/icons/ApplePodcastsIcon';

	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';
	import { SpotifyIcon } from '$global/assets/icons/SpotifyIcon';
	import { YouTubeIcon } from '$global/assets/icons/YouTubeIcon';
	import HeniTalksLogo from '$global/assets/logos/HeniTalksLogo/HeniTalksLogo.svelte';
	import { Button } from '$global/components/Button';
	import { Markdown } from '$global/components/Markdown';
	import { Txt } from '$global/components/Txt';
	import { VideoCard, type VideoCardProps } from '$global/components/VideoCard';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import type { GlobalHomePageData } from '$routes/types';

	const dataCy = 'home-talks';

	let data = $derived(page.data as GlobalHomePageData);
	let featuredTalks = $derived(data.featuredTalks);
	let podcasts = $derived(data.podcasts || []);

	let cards = $derived(
		featuredTalks?.slice(0, 4).reduce(
			(accumulator: VideoCardProps[], featuredTalk) => [
				...accumulator,
				{
					dataCy,
					href: `${GlobalRoutes.TalksHome}/${featuredTalk?.slug}`,
					duration: featuredTalk?.video_duration || '',
					title: featuredTalk?.title || '',
					description: '',
					src: getImageUrl(featuredTalk?.image_16_9?.filename_disk),
				},
			],
			[]
		) || []
	);

	const filmsCarouselOptions = {
		align: 'start',
		containScroll: 'trimSnaps',
		breakpoints: {
			'(min-width: 1280px)': { active: false },
		},
	} as const;

	const platformButtonClass =
		'h-9 w-full min-w-0 justify-center border-gray-700 bg-transparent px-2 text-gray-0 hover:border-gray-400 active:border-gray-400 focus:border-gray-400';

	let filmsCarouselViewport: HTMLDivElement | undefined = $state();
	let filmsEmblaApi: EmblaCarouselType | undefined = $state();
	let filmsCarouselState: HorizontalScrollState = $state(
		initialHorizontalScrollState
	);
	let destroyFilmsScrollHint: (() => void) | undefined;

	const handleFilmsCarouselInit = (event: CustomEvent<EmblaCarouselType>) => {
		destroyFilmsScrollHint?.();
		filmsEmblaApi = event.detail;

		if (filmsCarouselViewport) {
			destroyFilmsScrollHint = emblaScrollHint(
				filmsCarouselViewport,
				filmsEmblaApi,
				(state) => (filmsCarouselState = state)
			);
		}
	};

	onDestroy(() => destroyFilmsScrollHint?.());
</script>

{#snippet platformButtons(
	podcast: NonNullable<GlobalHomePageData['podcasts']>[number]
)}
	<div class="flex flex-col gap-2">
		<Txt variant="body3" class="text-gray-400">Watch or listen on:</Txt>

		<div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
			{#if podcast?.youtube_link}
				<a
					href={podcast.youtube_link}
					target="_blank"
					rel="noreferrer noopener"
					data-cy={`${dataCy}-podcast-${podcast?.slug}-youtube-link`}
					class="min-w-0"
				>
					<Button
						variant="secondary"
						size="md"
						icon
						class={platformButtonClass}
						classes={{
							label:
								'min-w-0 truncate text-gray-0 text-[0.75rem] xxs:text-[0.875rem]',
						}}
						dataCy={`${dataCy}-podcast-${podcast?.slug}-youtube`}
					>
						{#snippet leading()}
							<YouTubeIcon
								hexCode="#FF0000"
								class="mr-1 !h-[24px] !w-[24px] shrink-0"
							/>
						{/snippet}
						YouTube
					</Button>
				</a>
			{/if}

			{#if podcast?.spotify_link}
				<a
					href={podcast.spotify_link}
					target="_blank"
					rel="noreferrer noopener"
					data-cy={`${dataCy}-podcast-${podcast?.slug}-spotify-link`}
					class="min-w-0"
				>
					<Button
						variant="secondary"
						size="md"
						icon
						class={platformButtonClass}
						classes={{ label: 'min-w-0 truncate text-gray-0' }}
						dataCy={`${dataCy}-podcast-${podcast?.slug}-spotify`}
					>
						{#snippet leading()}
							<SpotifyIcon class="mr-1 !h-[20px] !w-[20px] shrink-0" />
						{/snippet}
						Spotify
					</Button>
				</a>
			{/if}

			{#if podcast?.apple_podcasts_link}
				<a
					href={podcast.apple_podcasts_link}
					target="_blank"
					rel="noreferrer noopener"
					data-cy={`${dataCy}-podcast-${podcast?.slug}-apple-link`}
					class="min-w-0"
				>
					<Button
						variant="secondary"
						size="md"
						icon
						class={platformButtonClass}
						classes={{ label: 'min-w-0 truncate text-gray-0' }}
						dataCy={`${dataCy}-podcast-${podcast?.slug}-apple`}
					>
						{#snippet leading()}
							<ApplePodcastsIcon class="mr-1 !h-[20px] !w-[20px] shrink-0" />
						{/snippet}
						Apple
					</Button>
				</a>
			{/if}
		</div>
	</div>
{/snippet}

{#snippet podcastDetails(
	podcast: NonNullable<GlobalHomePageData['podcasts']>[number]
)}
	<a
		href={`${GlobalRoutes.PodcastsHome}/${podcast?.slug}`}
		data-cy={`${dataCy}-podcast-${podcast?.slug}-title-link`}
		class="group inline-block"
	>
		<Txt variant="h6" class="text-gray-0 mb-1 group-hover:underline">
			{podcast?.title}
		</Txt>
	</a>

	{#if podcast?.short_description}
		<Markdown
			dataCy={`${dataCy}-podcast-${podcast?.slug}-description`}
			source={podcast.short_description}
			size="small"
			color="gray"
			class="line-clamp-2 xl:line-clamp-none [&_p]:text-gray-300 [&>p]:mb-0"
		/>
	{/if}

	{#if podcast?.episodes_func?.count}
		<Txt variant="label4" class="mt-3 text-gray-400">
			{podcast.episodes_func.count} episode{podcast.episodes_func.count === 1
				? ''
				: 's'} available<span class="hidden sm:inline"
				>{' '}to watch or listen</span
			>
		</Txt>
	{/if}

	<a
		href={`${GlobalRoutes.PodcastsHome}/${podcast?.slug}`}
		class="group mt-3 inline-block underline"
	>
		<Txt variant="label4" class="group-hover:text-gray-0 text-gray-300">
			View all episodes
		</Txt>
	</a>
{/snippet}

{#snippet podcastCard(
	podcast: NonNullable<GlobalHomePageData['podcasts']>[number]
)}
	<div
		data-cy={`${dataCy}-podcast-${podcast?.slug}`}
		class="grid min-w-0 grid-cols-2 overflow-hidden rounded border border-gray-700 sm:grid-cols-[150px_minmax(0,1fr)] xl:grid-cols-[180px_minmax(0,1fr)]"
	>
		<a
			href={`${GlobalRoutes.PodcastsHome}/${podcast?.slug}`}
			data-cy={`${dataCy}-podcast-${podcast?.slug}-image-link`}
			class="col-start-1 row-start-1 block min-w-0"
		>
			<img
				src={getImageUrl(podcast?.image?.filename_disk, '512px')}
				alt={podcast?.title || 'HENI podcast'}
				class="aspect-square h-full w-full object-cover"
				loading="lazy"
			/>
		</a>

		<div
			class="col-span-2 row-start-2 min-w-0 border-t border-gray-700 p-4 sm:col-span-1 sm:col-start-2 sm:row-start-1 sm:border-t-0 sm:border-l"
		>
			{@render podcastDetails(podcast)}
		</div>

		<div
			class="col-start-2 row-start-1 min-w-0 p-3 sm:col-span-2 sm:col-start-1 sm:row-start-2 sm:border-t sm:border-gray-700 sm:p-4"
		>
			{@render platformButtons(podcast)}
		</div>
	</div>
{/snippet}

<section
	data-cy={dataCy}
	class="-mx-4 min-w-0 overflow-hidden bg-gray-900 px-4 py-8 sm:mx-0 sm:rounded sm:px-6 lg:p-8"
>
	<div class="mb-8 flex min-w-0 items-start justify-between gap-6">
		<div class="min-w-0">
			<HeniTalksLogo color="gray-0" class="mb-2 w-[145px]" />
			<Txt variant="body3" class="text-gray-400">
				Watch and listen to stories of great art from around the world.
			</Txt>
		</div>

		<a
			data-cy={`${dataCy}-view-talks-top`}
			href={GlobalRoutes.TalksHome}
			class="hidden shrink-0 sm:block"
		>
			<Button dataCy={`${dataCy}-view-talks-top`} variant="secondary" size="md">
				View Talks
				{#snippet trailing()}<ChevronRightIcon />{/snippet}
			</Button>
		</a>
	</div>

	<div
		class={classNames(
			'grid min-w-0 gap-10',
			podcasts.length && 'xl:grid-cols-2 xl:gap-8'
		)}
	>
		<div class="min-w-0">
			<div class="mb-4 flex items-center justify-between gap-4">
				<Txt variant="h5" class="text-gray-0 xxs:text-[1.125rem] text-[1rem]"
					>Latest films</Txt
				>

				<div class="flex items-center gap-3">
					<a href={TalksRoutes.Gallery} class="group shrink-0">
						<Txt
							variant="label4"
							class="group-hover:text-gray-0 text-gray-400 underline"
						>
							View all films
						</Txt>
					</a>

					<HorizontalScrollControls
						emblaApi={filmsEmblaApi}
						state={filmsCarouselState}
						variant="inline"
						dark
						previousLabel="Show previous films"
						nextLabel="Show more films"
						class="xl:hidden"
					/>
				</div>
			</div>

			<div
				bind:this={filmsCarouselViewport}
				use:emblaCarouselSvelte={{
					options: filmsCarouselOptions,
					plugins: [],
				}}
				onemblaInit={handleFilmsCarouselInit}
				class="content-carousel-hint overflow-hidden"
				role="region"
				aria-roledescription="carousel"
				aria-label="Latest films"
			>
				<div class="flex gap-4 xl:grid xl:grid-cols-2">
					{#each cards as card, index}
						<VideoCard
							{...card}
							variant="dark"
							class={classNames(
								'min-w-0 flex-[0_0_84%] sm:flex-[0_0_47%] lg:flex-[0_0_31%] xl:flex-auto',
								index > 1 && 'xl:hidden'
							)}
						/>
					{/each}
				</div>
			</div>
		</div>

		{#if podcasts.length}
			<div data-cy={`${dataCy}-podcasts`} class="min-w-0">
				<div class="mb-4 flex items-center justify-between gap-4">
					<Txt variant="h5" class="text-gray-0">Podcasts</Txt>

					<a
						data-cy={`${dataCy}-view-all-podcasts-link`}
						href={GlobalRoutes.PodcastsHome}
						class="group shrink-0"
					>
						<Txt
							variant="label4"
							class="group-hover:text-gray-0 text-gray-400 underline"
						>
							View all podcasts
						</Txt>
					</a>
				</div>

				{@render podcastCard(podcasts[0])}

				<a
					data-cy={`${dataCy}-view-talks-bottom`}
					href={GlobalRoutes.TalksHome}
					class="mt-4 block sm:hidden"
				>
					<Button
						dataCy={`${dataCy}-view-talks-bottom`}
						variant="secondary"
						size="md"
						class="w-full justify-center"
					>
						View HENI Talks
						{#snippet trailing()}<ChevronRightIcon />{/snippet}
					</Button>
				</a>
			</div>
		{/if}
	</div>
</section>
